import {useNotificationContext} from 'contexts/NotificationContext';
import useSizePercentage from 'hooks/Product/DoorDesign';
import {isArray} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {Form, InputGroup} from 'react-bootstrap';
import {InputGroupText} from 'shared/components/Forms/Size';
import {validationErrorAppliesToField} from 'shared/validator';

const parseDisplayValue = (value) => {
    // Check if the value is a percentage and convert it to a decimal
    if (typeof value === 'string' && value.includes('%')) {
        return parseFloat(value) / 100;
    }
    // Otherwise, parse it as a float
    return parseFloat(value);
};

export const SizePercentage = ({
    name,
    fieldsetName,
    selectHandler,
    value,
    metric,
    disabled = false,
    ...otherParams
}) => {
    const [isInvalid, setIsInvalid] = useState('');
    const {messages} = useNotificationContext();

    const {
        displayValue,
        decimalValue,
        setDisplayValue,
        setShowPercentage,
        showPercentage,
        setDecimalValue,
        heightReference,
    } = useSizePercentage(value, fieldsetName);

    const percentageHandlerFocus = () => {
        if (showPercentage) {
            setDisplayValue(+decimalValue.toFixed(4));
        }
    };

    const percentageHandlerBlur = () => {
        const localValue = parseFloat(displayValue);

        if (localValue < 0) {
            setDisplayValue(0);
            setDecimalValue(0);
            selectHandler(name, 0);
            setShowPercentage(true);
        } else {
            if (localValue < 1) {
                if (localValue == value) {
                    setDisplayValue(`${+(decimalValue * 100).toFixed(4)}%`);
                } else {
                    selectHandler(name, localValue);
                    setShowPercentage(true);
                }
            } else {
                if (localValue > heightReference) {
                    selectHandler(name, localValue);
                } else {
                    if (localValue != value) {
                        selectHandler(name, localValue);
                        setShowPercentage(false);
                    }
                }
            }
        }
    };

    const isFieldInvalid = useMemo(() => {
        return (
            otherParams?.isInvalid &&
            parseDisplayValue(displayValue) > heightReference
        );
    }, [otherParams?.isInvalid, displayValue, heightReference]);

    useEffect(() => {
        if (isFieldInvalid) {
            setIsInvalid(`Value cannot be greater than ${heightReference}`);
        } else if (otherParams?.isInvalid) {
            const errorMessages = isArray(messages.errors)
                ? messages.errors
                      .filter((error) =>
                          validationErrorAppliesToField(
                              error,
                              name,
                              otherParams.fieldSetIndex
                          )
                      )
                      .map((error) => error.message)
                : [];

            if (errorMessages.length) {
                setIsInvalid(errorMessages[0]);
            }
        } else {
            setIsInvalid('');
        }
    }, [isFieldInvalid, heightReference, otherParams?.isInvalid, messages]);

    return (
        <InputGroup className="cbc-size">
            <Form.Control
                disabled={disabled}
                onFocus={percentageHandlerFocus}
                onBlur={percentageHandlerBlur}
                name={name}
                value={displayValue}
                onChange={(event) => {
                    setDisplayValue(event.target.value);
                }}
                isInvalid={isInvalid != '' ? true : false}
            />
            {displayValue >= 1 ? (
                <InputGroupText>{metric}</InputGroupText>
            ) : (
                <></>
            )}

            {isInvalid != '' && (
                <Form.Control.Feedback type="invalid">
                    {isInvalid}
                </Form.Control.Feedback>
            )}
        </InputGroup>
    );
};
